import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import './MazeSolver.css';

const MAZE_SIZE = 15;
const CELL_WALL = 1;
const CELL_PATH = 0;
const CELL_START = 2;
const CELL_END = 3;

const MazeSolver = () => {
  const [maze, setMaze] = useState([]);
  const [playerPos, setPlayerPos] = useState({ x: 1, y: 1 });
  const [gameWon, setGameWon] = useState(false);
  const [moves, setMoves] = useState(0);
  const [gameActive, setGameActive] = useState(false);

  const generateMaze = () => {
    // Create a maze filled with walls
    const newMaze = Array(MAZE_SIZE).fill().map(() => Array(MAZE_SIZE).fill(CELL_WALL));
    
    // Recursive backtracking algorithm to create connected paths
    const stack = [];
    const visited = Array(MAZE_SIZE).fill().map(() => Array(MAZE_SIZE).fill(false));
    
    // Start from position (1,1)
    let currentX = 1;
    let currentY = 1;
    newMaze[currentY][currentX] = CELL_PATH;
    visited[currentY][currentX] = true;
    stack.push([currentX, currentY]);
    
    const directions = [
      [0, -2], // up
      [2, 0],  // right
      [0, 2],  // down
      [-2, 0]  // left
    ];
    
    while (stack.length > 0) {
      const neighbors = [];
      
      // Find unvisited neighbors
      for (const [dx, dy] of directions) {
        const newX = currentX + dx;
        const newY = currentY + dy;
        
        if (newX > 0 && newX < MAZE_SIZE - 1 && 
            newY > 0 && newY < MAZE_SIZE - 1 && 
            !visited[newY][newX]) {
          neighbors.push([newX, newY, dx, dy]);
        }
      }
      
      if (neighbors.length > 0) {
        // Choose random neighbor
        const [nextX, nextY, dx, dy] = neighbors[Math.floor(Math.random() * neighbors.length)];
        
        // Create path to neighbor
        newMaze[currentY + dy / 2][currentX + dx / 2] = CELL_PATH;
        newMaze[nextY][nextX] = CELL_PATH;
        visited[nextY][nextX] = true;
        
        stack.push([nextX, nextY]);
        currentX = nextX;
        currentY = nextY;
      } else {
        // Backtrack
        [currentX, currentY] = stack.pop();
      }
    }
    
    // Set start and end positions
    newMaze[1][1] = CELL_START;
    newMaze[MAZE_SIZE - 2][MAZE_SIZE - 2] = CELL_END;
    
    return newMaze;
  };

  const startGame = () => {
    const newMaze = generateMaze();
    setMaze(newMaze);
    setPlayerPos({ x: 1, y: 1 });
    setGameWon(false);
    setMoves(0);
    setGameActive(true);
  };

  const movePlayer = useCallback((dx, dy) => {
    if (!gameActive || gameWon) return;

    const newX = playerPos.x + dx;
    const newY = playerPos.y + dy;

    // Check bounds and walls
    if (newX >= 0 && newX < MAZE_SIZE && 
        newY >= 0 && newY < MAZE_SIZE && 
        maze[newY] && maze[newY][newX] !== CELL_WALL) {
      
      setPlayerPos({ x: newX, y: newY });
      setMoves(prev => prev + 1);
      
      // Check if reached the end
      if (maze[newY][newX] === CELL_END) {
        setGameWon(true);
        setGameActive(false);
      }
    }
  }, [gameActive, gameWon, playerPos, maze]);

  const handleKeyPress = useCallback((e) => {
    switch (e.key.toLowerCase()) {
      case 'w':
        movePlayer(0, -1);
        break;
      case 's':
        movePlayer(0, 1);
        break;
      case 'a':
        movePlayer(-1, 0);
        break;
      case 'd':
        movePlayer(1, 0);
        break;
      default:
        break;
    }
  }, [movePlayer]);

  useEffect(() => {
    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [handleKeyPress]);

  const getCellClass = (x, y) => {
    if (playerPos.x === x && playerPos.y === y) return 'maze-cell player';
    
    switch (maze[y]?.[x]) {
      case CELL_WALL: return 'maze-cell wall';
      case CELL_PATH: return 'maze-cell path';
      case CELL_START: return 'maze-cell start';
      case CELL_END: return 'maze-cell end';
      default: return 'maze-cell wall';
    }
  };

  const getCellContent = (x, y) => {
    if (playerPos.x === x && playerPos.y === y) return '🚶';
    
    switch (maze[y]?.[x]) {
      case CELL_START: return '🏁';
      case CELL_END: return '🎯';
      default: return '';
    }
  };

  return (
    <div className="game-container">
      <div className="game-header">
        <h1 className="game-title-header">🌀 Maze Solver</h1>
        <div className="game-instructions">
          Use WASD keys to navigate from 🏁 to 🎯. Find the shortest path!
        </div>
      </div>

      <div className="game-board">
        <div className="score-display">
          Moves: {moves}
          {gameWon && <div style={{color: 'green'}}>🎉 Maze Solved!</div>}
        </div>
        
        <div className="game-play-area">
          <div className="maze-grid">
            {maze.map((row, y) =>
              row.map((cell, x) => (
                <div
                  key={`${x}-${y}`}
                  className={getCellClass(x, y)}
                >
                  {getCellContent(x, y)}
                </div>
              ))
            )}
          </div>
        </div>

        {gameWon && (
          <div className="game-over">
            <h2>🎉 Congratulations!</h2>
            <p>You solved the maze in {moves} moves!</p>
          </div>
        )}

        <div className="game-controls">
          <button className="btn btn-primary" onClick={startGame}>
            {gameWon ? 'New Maze' : 'Start Game'}
          </button>
          <Link to="/" className="btn btn-secondary">
            Back to Home
          </Link>
        </div>
      </div>
    </div>
  );
};

export default MazeSolver;